(async () => {
  const { default: PhotoSwipeLightbox } = await import(window.__pswpLightboxUrl);

  function buildVideoHtml(videoUrl, host, width, height) {
    const ratio = height / width;
    const maxW = Math.min(width, 1200);
    const maxH = Math.round(maxW * ratio);

    if (host === 'youtube' || host === 'vimeo') {
      let embedUrl = videoUrl;
      if (host === 'youtube' && videoUrl.includes('watch?v=')) {
        embedUrl = videoUrl.replace('watch?v=', 'embed/');
      }
      if (!embedUrl.includes('autoplay')) {
        embedUrl += (embedUrl.includes('?') ? '&' : '?') + 'autoplay=1';
      }
      return `<div class="pswp-video-container" style="width:${maxW}px;max-width:90vw;">
        <div style="position:relative;padding-bottom:${(ratio * 100).toFixed(2)}%;height:0;overflow:hidden;">
          <iframe src="${embedUrl}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;" allow="autoplay;encrypted-media;fullscreen" allowfullscreen></iframe>
        </div>
      </div>`;
    }

    return `<div class="pswp-video-container" style="width:${maxW}px;max-width:90vw;">
      <video src="${videoUrl}" controls autoplay playsinline style="width:100%;height:auto;display:block;border-radius:4px;"></video>
    </div>`;
  }

  function collectItems(container) {
    const items = [];
    container.querySelectorAll('[data-pswp-trigger]').forEach((btn) => {
      const wrap = btn.closest('.gallery-media-wrap');
      const img = wrap ? wrap.querySelector('img') : null;
      const mediaType = btn.dataset.pswpType || 'image';
      const w = parseInt(btn.dataset.pswpWidth, 10) || 1200;
      const h = parseInt(btn.dataset.pswpHeight, 10) || 1200;

      if (mediaType === 'video' || mediaType === 'external_video') {
        const videoUrl = btn.dataset.pswpVideoUrl || '';
        const host = btn.dataset.pswpVideoHost || '';
        items.push({
          html: buildVideoHtml(videoUrl, host, w, h),
          msrc: img ? (img.currentSrc || img.src) : '',
          thumbEl: img,
          triggerEl: btn,
        });
      } else {
        items.push({
          src: btn.dataset.pswpSrc,
          width: w,
          height: h,
          msrc: img ? (img.currentSrc || img.src) : '',
          thumbEl: img,
          triggerEl: btn,
        });
      }
    });
    return items;
  }

  let lastOpen = 0;
  function openLightbox(container, clickedTrigger) {
    const now = Date.now();
    if (now - lastOpen < 600) return;
    lastOpen = now;
    const items = collectItems(container);
    const index = items.findIndex((item) => item.triggerEl === clickedTrigger);
    if (index < 0 || !items.length) return;

    const lightbox = new PhotoSwipeLightbox({
      dataSource: items,
      pswpModule: () => import(window.__pswpCoreUrl),
      bgOpacity: 0.92,
      showHideAnimationType: 'zoom',
      closeOnVerticalDrag: true,
      pinchToClose: true,
      imageClickAction: 'zoom',
      tapAction: 'zoom',
      doubleTapAction: 'zoom',
      initialZoomLevel: 'fit',
      secondaryZoomLevel: 2,
      maxZoomLevel: 4,
      wheelToZoom: true,
      arrowKeys: true,
    });

    lightbox.addFilter('thumbEl', (_thumb, itemData) => {
      return itemData.thumbEl || _thumb;
    });

    lightbox.on('close', () => {
      if (document.activeElement) document.activeElement.blur();
    });

    lightbox.on('change', () => {
      const pswp = lightbox.pswp;
      if (!pswp) return;
      pswp.element.querySelectorAll('.pswp-video-container video, .pswp-video-container iframe').forEach((el) => {
        const slide = el.closest('.pswp__item');
        if (slide && slide.getAttribute('aria-hidden') === 'true') {
          if (el.tagName === 'VIDEO') el.pause();
          if (el.tagName === 'IFRAME') el.src = el.src;
        }
      });
    });

    lightbox.on('destroy', () => {
      document.querySelectorAll('.pswp .pswp-video-container video').forEach((v) => v.pause());
      document.querySelectorAll('.pswp .pswp-video-container iframe').forEach((f) => { f.src = ''; });
    });

    lightbox.init();
    lightbox.loadAndOpen(index);
  }

  function bindTrigger(container, btn) {
    if (btn._pswpTriggerBound) return;
    btn._pswpTriggerBound = true;

    const open = (e) => {
      e.preventDefault();
      e.stopPropagation();
      openLightbox(container, btn);
    };

    btn.addEventListener('click', open);

    // Mobile: Splide swallows click after touchend, bind touchend directly on the button
    btn.addEventListener('touchend', (e) => {
      if (btn._touchMoved) return; // ignore swipe
      open(e);
    });
    btn.addEventListener('touchstart', () => { btn._touchMoved = false; }, { passive: true });
    btn.addEventListener('touchmove', () => { btn._touchMoved = true; }, { passive: true });
  }

  function initGallery(container) {
    if (!container || container._pswpBound) return;
    container._pswpBound = true;

    container.querySelectorAll('[data-pswp-trigger]').forEach((btn) => {
      bindTrigger(container, btn);
    });

    // A tap or click on the photo itself opens the same full-screen zoom as the zoom button.
    const triggerFor = (target) => {
      if (!target || !target.closest) return null;
      if (target.closest('[data-pswp-trigger], a, button, video, iframe, model-viewer')) return null;
      const wrap = target.closest('.gallery-media-wrap');
      return wrap ? wrap.querySelector('[data-pswp-trigger]') : null;
    };
    let startX = 0;
    let startY = 0;
    let moved = false;
    container.addEventListener('touchstart', (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      startX = t.clientX;
      startY = t.clientY;
      moved = false;
    }, { passive: true });
    container.addEventListener('touchmove', (e) => {
      const t = e.touches && e.touches[0];
      if (t && (Math.abs(t.clientX - startX) > 8 || Math.abs(t.clientY - startY) > 8)) moved = true;
    }, { passive: true });
    container.addEventListener('touchend', (e) => {
      if (moved) return;
      const btn = triggerFor(e.target);
      if (!btn) return;
      e.preventDefault();
      openLightbox(container, btn);
    });
    container.addEventListener('click', (e) => {
      const btn = triggerFor(e.target);
      if (!btn) return;
      e.preventDefault();
      openLightbox(container, btn);
    });
  }

  document.querySelectorAll('.product-gallery-main, .gallery-stacked-images').forEach(initGallery);

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('.product-gallery-main, .gallery-stacked-images')) {
          initGallery(node);
        }
        node.querySelectorAll?.('.product-gallery-main, .gallery-stacked-images').forEach(initGallery);
        // Also rebind any newly added triggers inside existing containers
        node.querySelectorAll?.('[data-pswp-trigger]').forEach((btn) => {
          const container = btn.closest('.product-gallery-main, .gallery-stacked-images');
          if (container) bindTrigger(container, btn);
        });
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
